import { useEffect, useRef, useState } from "react";
import type { BookCreateInput, BookDetail } from "@atlas/contracts";
import { ApiError, createBook } from "../lib/api";
import { Sheet } from "./ui/Sheet";
import { Button } from "./ui/Button";
import { Field, Input, Textarea } from "./ui/Input";
import { ChipGroup } from "./ui/Select";
import { Alert } from "./ui/Alert";

interface AddBookSheetProps {
  open: boolean;
  onClose: () => void;
  /** Called with the created BookDetail; the caller is responsible for
   * closing the sheet, refreshing the list, and navigating to the detail
   * page. */
  onCreated: (book: BookDetail) => void;
}

type BookStatus = "unread" | "reading" | "read";

interface FormState {
  titleFa: string;
  authors: string;
  translators: string;
  genres: string;
  status: BookStatus;
  totalPages: string;
  currentPage: string;
  rating: string;
  location: string;
  year: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  titleFa: "",
  authors: "",
  translators: "",
  genres: "",
  status: "unread",
  totalPages: "",
  currentPage: "",
  rating: "",
  location: "",
  year: "",
  notes: "",
};

const STATUS_OPTIONS: Array<{ label: string; value: BookStatus }> = [
  { label: "نخوانده", value: "unread" },
  { label: "در حال خواندن", value: "reading" },
  { label: "خوانده‌شده", value: "read" },
];

type BuildResult = { ok: true; payload: BookCreateInput } | { ok: false; error: string };

function splitList(value: string): string[] {
  return value
    .split(/[,،]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function buildPayload(form: FormState): BuildResult {
  const titleFa = form.titleFa.trim();
  if (!titleFa) {
    return { ok: false, error: "عنوان کتاب را وارد کنید." };
  }

  const payload: BookCreateInput = { titleFa, status: form.status };

  const authors = splitList(form.authors);
  if (authors.length > 0) payload.authors = authors;
  const translators = splitList(form.translators);
  if (translators.length > 0) payload.translators = translators;
  const genres = splitList(form.genres);
  if (genres.length > 0) payload.genres = genres;

  if (form.rating.trim()) {
    const rating = Number(form.rating.trim());
    if (!Number.isInteger(rating) || rating < 0 || rating > 10) {
      return { ok: false, error: "امتیاز باید عددی صحیح بین ۰ تا ۱۰ باشد." };
    }
    payload.rating = rating;
  }

  let totalPages: number | undefined;
  if (form.totalPages.trim()) {
    totalPages = Number(form.totalPages.trim());
    if (!Number.isInteger(totalPages) || totalPages < 0) {
      return { ok: false, error: "تعداد کل صفحات باید عددی صحیح و نامنفی باشد." };
    }
    payload.totalPages = totalPages;
  }

  if (form.currentPage.trim()) {
    const currentPage = Number(form.currentPage.trim());
    if (!Number.isInteger(currentPage) || currentPage < 0) {
      return { ok: false, error: "صفحهٔ فعلی باید عددی صحیح و نامنفی باشد." };
    }
    if (totalPages !== undefined && currentPage > totalPages) {
      return { ok: false, error: "صفحهٔ فعلی نمی‌تواند بیشتر از تعداد کل صفحات باشد." };
    }
    payload.currentPage = currentPage;
  }

  if (form.year.trim()) payload.year = form.year.trim();
  if (form.location.trim()) payload.location = form.location.trim();
  if (form.notes.trim()) payload.notes = form.notes.trim();

  return { ok: true, payload };
}

/** Mobile bottom-sheet form for manually creating a book (Books FAB → «افزودن
 * کتاب»). No cover upload in this slice -- new books render without one. */
export function AddBookSheet({ open, onClose, onCreated }: AddBookSheetProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh key per intended submit, reused only when retrying the exact
  // same payload (submittedPayloadRef tracks what that was); rotated as
  // soon as any field changes after a submit attempt, so a materially
  // different retry never replays the old key.
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const submittedPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setBusy(false);
    idempotencyKeyRef.current = crypto.randomUUID();
    submittedPayloadRef.current = null;
  }, [open]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (submittedPayloadRef.current !== null) {
      idempotencyKeyRef.current = crypto.randomUUID();
      submittedPayloadRef.current = null;
    }
  }

  async function submit() {
    if (busy) return;
    const result = buildPayload(form);
    if (!result.ok) {
      setError(result.error);
      return;
    }

    const serialized = JSON.stringify(result.payload);
    if (submittedPayloadRef.current !== serialized) {
      idempotencyKeyRef.current = crypto.randomUUID();
      submittedPayloadRef.current = serialized;
    }

    setBusy(true);
    setError(null);
    try {
      const book = await createBook(result.payload, idempotencyKeyRef.current);
      onCreated(book);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("این درخواست با اطلاعات متفاوتی قبلاً ثبت شده. لطفاً فرم را دوباره بررسی و ارسال کنید.");
      } else {
        setError("ثبت کتاب با خطا مواجه شد. اتصال یا اطلاعات وارد شده را بررسی کنید.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="افزودن کتاب">
      {error && <Alert tone="danger">{error}</Alert>}

      <Field label="عنوان" htmlFor="add-book-title-fa">
        <Input
          id="add-book-title-fa"
          value={form.titleFa}
          onChange={(e) => updateField("titleFa", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="نویسندگان (با ویرگول جدا کنید)" htmlFor="add-book-authors">
        <Input
          id="add-book-authors"
          value={form.authors}
          onChange={(e) => updateField("authors", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="مترجمان (با ویرگول جدا کنید)" htmlFor="add-book-translators">
        <Input
          id="add-book-translators"
          value={form.translators}
          onChange={(e) => updateField("translators", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="ژانرها (با ویرگول جدا کنید)" htmlFor="add-book-genres">
        <Input
          id="add-book-genres"
          value={form.genres}
          onChange={(e) => updateField("genres", e.target.value)}
          disabled={busy}
        />
      </Field>
      <div className="field">
        <p className="field__label">وضعیت</p>
        <ChipGroup
          options={STATUS_OPTIONS}
          value={form.status}
          onChange={(value) => updateField("status", value)}
          ariaLabel="وضعیت"
        />
      </div>
      <Field label="تعداد کل صفحات" htmlFor="add-book-total-pages">
        <Input
          id="add-book-total-pages"
          type="number"
          dir="ltr"
          value={form.totalPages}
          onChange={(e) => updateField("totalPages", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="صفحهٔ فعلی" htmlFor="add-book-current-page">
        <Input
          id="add-book-current-page"
          type="number"
          dir="ltr"
          value={form.currentPage}
          onChange={(e) => updateField("currentPage", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="امتیاز (۰ تا ۱۰)" htmlFor="add-book-rating">
        <Input
          id="add-book-rating"
          type="number"
          dir="ltr"
          value={form.rating}
          onChange={(e) => updateField("rating", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="محل نگهداری" htmlFor="add-book-location">
        <Input
          id="add-book-location"
          value={form.location}
          onChange={(e) => updateField("location", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="سال انتشار" htmlFor="add-book-year">
        <Input
          id="add-book-year"
          dir="ltr"
          value={form.year}
          onChange={(e) => updateField("year", e.target.value)}
          disabled={busy}
        />
      </Field>
      <Field label="یادداشت" htmlFor="add-book-notes">
        <Textarea
          id="add-book-notes"
          rows={3}
          value={form.notes}
          onChange={(e) => updateField("notes", e.target.value)}
          disabled={busy}
        />
      </Field>

      <div className="sheet-actions">
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          انصراف
        </Button>
        <Button type="button" variant="primary" onClick={submit} disabled={busy}>
          افزودن کتاب
        </Button>
      </div>
    </Sheet>
  );
}
